/**
 * Quais conversas ganharam mensagem NOVA entre duas leituras da lista — é o
 * que decide o aviso (toast, som, Notification) de "Nova mensagem".
 *
 * Antes o aviso disparava quando a SOMA de `unread_count` subia. Com "Marcar
 * como não lida" a soma sobe sem mensagem nenhuma, e a equipe ouviria o som da
 * própria ação. A diferença está em `last_message_at`: mensagem recebida
 * sempre o carimba (`increment_unread`), marcar como não lida nunca. Então:
 * conversa nova já com não lida, ou não lida que subiu JUNTO com
 * `last_message_at`.
 *
 * `anterior = null` é a primeira leitura (abrir o CRM): não há "novo" ainda.
 */
export interface EstadoDeLeitura {
  unread_count: number;
  last_message_at: string;
}

export function fotografarLeitura<T extends EstadoDeLeitura & { id: string }>(
  conversas: readonly T[],
): Map<string, EstadoDeLeitura> {
  return new Map(conversas.map((c) => [c.id, { unread_count: c.unread_count || 0, last_message_at: c.last_message_at }]));
}

export function mensagensNovas<T extends EstadoDeLeitura & { id: string }>(
  anterior: ReadonlyMap<string, EstadoDeLeitura> | null,
  atual: readonly T[],
): T[] {
  if (!anterior) return [];
  // Conversa que não estava na foto só é "nova" se a última mensagem dela é
  // mais recente que tudo o que a foto tinha — senão é só o escopo de clubes
  // que mudou e trouxe conversas antigas para a lista.
  let maisRecenteAntes = '';
  for (const e of anterior.values()) if (e.last_message_at > maisRecenteAntes) maisRecenteAntes = e.last_message_at;
  return atual
    .filter((c) => {
      const naoLidas = c.unread_count || 0;
      if (naoLidas <= 0) return false;
      const antes = anterior.get(c.id);
      if (!antes) return c.last_message_at > maisRecenteAntes;
      return naoLidas > antes.unread_count && c.last_message_at !== antes.last_message_at;
    })
    .sort((a, b) => (a.last_message_at < b.last_message_at ? 1 : a.last_message_at > b.last_message_at ? -1 : 0));
}
